import {
  ApiQuestDetail,
  ApiQuestSettingsPayload,
  ApiUpdateQuestPayload,
} from "griptonite-api-client/types";
import isEqual from "lodash/isEqual";
import { z } from "zod";

const MediaSchema = z.object({ uuid: z.string(), url: z.string(), type: z.string() }).nullable();

const NotificationSchema = z.object({
  enabled: z.boolean(),
  text: z.string().max(120, { message: "Max 120 characters" }),
});

export const QuestPromotionFormSchema = z.object({
  notifications: z.object({
    questStart: NotificationSchema,
    questEndingSoon: NotificationSchema,
    checkinPromo: NotificationSchema,
  }),
  tvTakeover: z
    .object({
      enabled: z.boolean(),
      media: MediaSchema,
      frequency: z.enum(["LOW", "MEDIUM", "HIGH"]),
    })
    .superRefine((obj, ctx) => {
      if (obj.enabled && !obj.media) {
        ctx.addIssue({
          message: "Media required when TV advertising is enabled",
          code: z.ZodIssueCode.custom,
          path: ["media"],
        });
      }
    }),
  feedCard: z.object({
    enabled: z.boolean(),
    headline: z.string(),
    text: z.string(),
    image: MediaSchema,
  }),
});

export type QuestPromotionFormValues = z.infer<typeof QuestPromotionFormSchema>;
export type QuestNotificationName = keyof QuestPromotionFormValues["notifications"];

const NOTIFICATION_NAMES: QuestNotificationName[] = [
  "questStart",
  "questEndingSoon",
  "checkinPromo",
];

// A blank text means the server's own default, which it translates into each climber's language,
// so the form leaves texts blank rather than filling in the defaults.
export const questToPromotionFormValues = (quest: ApiQuestDetail): QuestPromotionFormValues => {
  const { notifications, tvTakeover, feedCard } = quest.settings;
  const notification = (name: QuestNotificationName) => ({
    enabled: notifications[name].enabled,
    text: notifications[name].text ?? "",
  });
  return {
    notifications: {
      questStart: notification("questStart"),
      questEndingSoon: notification("questEndingSoon"),
      checkinPromo: notification("checkinPromo"),
    },
    tvTakeover: {
      enabled: tvTakeover.enabled,
      media: quest.tvTakeoverMedia,
      frequency: tvTakeover.frequency,
    },
    feedCard: {
      enabled: feedCard.enabled,
      headline: feedCard.headline ?? "",
      text: feedCard.text ?? "",
      image: quest.feedCardImage,
    },
  };
};

// The API merges `settings` into what's stored, so only the sections that changed are sent.
export const questPromotionFormValuesToPayload = (
  values: QuestPromotionFormValues,
  initial: QuestPromotionFormValues,
): ApiUpdateQuestPayload => {
  const payload: ApiUpdateQuestPayload = {};
  const settings: ApiQuestSettingsPayload = {};

  for (const name of NOTIFICATION_NAMES) {
    if (!isEqual(values.notifications[name], initial.notifications[name])) {
      settings.notifications = {
        ...settings.notifications,
        [name]: {
          enabled: values.notifications[name].enabled,
          text: values.notifications[name].text || null,
        },
      };
    }
  }

  const { media: tvMedia, ...tvTakeover } = values.tvTakeover;
  const { media: initialTvMedia, ...initialTvTakeover } = initial.tvTakeover;
  if (!isEqual(tvTakeover, initialTvTakeover)) settings.tvTakeover = tvTakeover;
  if (tvMedia?.uuid !== initialTvMedia?.uuid) payload.tvTakeoverMedia = tvMedia?.uuid ?? null;

  const { image: feedImage, ...feedCard } = values.feedCard;
  const { image: initialFeedImage, ...initialFeedCard } = initial.feedCard;
  if (!isEqual(feedCard, initialFeedCard)) {
    settings.feedCard = {
      enabled: feedCard.enabled,
      headline: feedCard.headline || null,
      text: feedCard.text || null,
    };
  }
  if (feedImage?.uuid !== initialFeedImage?.uuid) payload.feedCardImage = feedImage?.uuid ?? null;

  if (Object.keys(settings).length) payload.settings = settings;
  return payload;
};
